import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FireService } from './fire.service';

export interface CompilationState {
  questionnaireCode: string;
  step: number;
  answers: { [questionCode: string]: any };
  comments?: { [questionCode: string]: string };
  times?: { [questionCode: string]: number };
  lastUpdate: number;
  synced: boolean;
  offline: boolean;
}

export enum SaveStatus {
  IDLE = 'idle',
  SAVING_LOCAL = 'saving_local',
  SAVING_BACKEND = 'saving_backend',
  SAVED = 'saved',
  ERROR = 'error',
  OFFLINE = 'offline'
}

@Injectable({
  providedIn: 'root'
})
export class CompilationSyncService {
  private readonly STORAGE_PREFIX = 'questionnaire_';
  private readonly SYNC_DEBOUNCE_MS = 6000;
  private readonly MIN_SYNC_INTERVAL_MS = 5000;
  private readonly MAX_RETRY_ATTEMPTS = 2;

  private readonly MAX_CONSECUTIVE_FAILURES = 2;

  private lastSyncAt = 0;
  private retryTimer: any = null;
  private consecutiveFailures = 0;
  private effectivelyOffline = false;

  private saveStatus$ = new BehaviorSubject<SaveStatus>(SaveStatus.IDLE);
  private syncQueue: Array<{ data: any; retries: number }> = [];
  private syncInProgress = false;
  private debounceTimer: any;

  // Per-questionnaire flag: una volta che l'intestazione è stata scritta sul backend
  // le successive scritture inviano solo un payload "slim" con merge.
  private firstSyncDone: { [questionnaireCode: string]: boolean } = {};

  // Ultimo payload pendente per coalescing: se un sync è in corso, non accodiamo
  // più scritture, accumuliamo solo l'ultimo onlineData e ri-schediamo.
  private pendingSync: { questionnaireCode: string; user: any; onlineData: any } | null = null;

  // Set di codici per cui la finalizzazione è in corso: qualsiasi debounce/sync
  // viene bloccato per evitare race con la scrittura finale di lastSave().
  private finalizing: { [questionnaireCode: string]: boolean } = {};

  // Hash dell'ultimo payload slim sincronizzato per codice. Permette di
  // skippare scritture identiche (es. focus/blur senza cambi reali) evitando
  // costi Firestore inutili a 100k utenti concorrenti.
  private lastSyncedHash: { [questionnaireCode: string]: string } = {};

  // Observable per lo stato di salvataggio (per UI)
  public saveStatus: Observable<SaveStatus> = this.saveStatus$.asObservable();

  constructor(private fire: FireService) {
    // Verifica connessione e gestisce sync automatica
    this.setupOnlineListener();
    this.processSyncQueue();
  }

  /**
   * Salva immediatamente in locale e programma sync backend
   */
  saveAnswer(
    questionnaireCode: string,
    questionCode: string,
    answer: any,
    _user: any,
    _onlineData: any
  ): void {
    // Salvataggio locale only — il backend sync viene fatto solo su
    // avanzamento domanda via saveCompilationState() per ridurre il
    // numero di scritture e prevenire "Write stream exhausted".
    const state = this.getLocalState(questionnaireCode) || {
      questionnaireCode,
      step: 0,
      answers: {},
      lastUpdate: Date.now(),
      synced: false,
      offline: false
    };

    state.answers[questionCode] = answer;
    state.lastUpdate = Date.now();
    state.synced = false;

    this.saveLocalState(questionnaireCode, state);
    this.saveStatus$.next(SaveStatus.SAVING_LOCAL);
  }

  /**
   * Salvataggio locale puro (no debounce, no backend, no status emit).
   * Usato da beforeunload dove le scritture async non sono affidabili.
   */
  saveLocalOnly(
    questionnaireCode: string,
    step: number,
    answers: { [questionCode: string]: any },
    comments?: { [questionCode: string]: string },
    times?: { [questionCode: string]: number }
  ): void {
    try {
      const existing = this.getLocalState(questionnaireCode);
      const state: CompilationState = {
        questionnaireCode,
        step,
        answers,
        comments: comments || existing?.comments || {},
        times: times || existing?.times || {},
        lastUpdate: Date.now(),
        synced: false,
        offline: !navigator.onLine
      };
      this.saveLocalState(questionnaireCode, state);
    } catch {
      // never throw from unload path
    }
  }

  /**
   * Salva stato completo della compilazione
   */
  saveCompilationState(
    questionnaireCode: string,
    step: number,
    answers: { [questionCode: string]: any },
    user: any,
    onlineData: any,
    comments?: { [questionCode: string]: string },
    times?: { [questionCode: string]: number }
  ): void {
    const existing = this.getLocalState(questionnaireCode);
    const state: CompilationState = {
      questionnaireCode,
      step,
      answers,
      comments: comments || {},
      times: times || existing?.times || {},
      lastUpdate: Date.now(),
      synced: false,
      offline: !navigator.onLine
    };

    this.saveLocalState(questionnaireCode, state);
    this.saveStatus$.next(SaveStatus.SAVING_LOCAL);

    this.debounceBackendSync(questionnaireCode, user, onlineData);
  }

  /**
   * Recupera stato locale se esiste
   */
  getLocalState(questionnaireCode: string): CompilationState | null {
    try {
      const stored = localStorage.getItem(`${this.STORAGE_PREFIX}${questionnaireCode}`);
      if (stored) {
        const state = JSON.parse(stored);
        // Verifica validità (non più vecchio di 7 giorni)
        const maxAge = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - state.lastUpdate < maxAge) {
          return state;
        } else {
          // Rimuovi stato troppo vecchio
          this.clearLocalState(questionnaireCode);
        }
      }
    } catch (error) {
      console.debug('Error reading local state:', error);
    }
    return null;
  }

  /**
   * Verifica se esiste uno stato locale valido
   */
  hasLocalState(questionnaireCode: string): boolean {
    return this.getLocalState(questionnaireCode) !== null;
  }

  /**
   * Ripristina stato da locale
   */
  restoreFromLocal(questionnaireCode: string): CompilationState | null {
    const state = this.getLocalState(questionnaireCode);
    if (state) {
      this.saveStatus$.next(SaveStatus.SAVED);
    }
    return state;
  }

  /**
   * Costruisce payload "slim" per le scritture incrementali.
   * Contiene solo i campi che cambiano durante la compilazione.
   * Con merge: true l'intestazione (azienda, licenza, questionario, dati anagrafici,
   * lang, ecc.) resta preservata sul documento Firestore.
   */
  private buildSlimPayload(onlineData: any): any {
    // Ultra-slim: solo risposte e tempo. Commento e ritorni vengono scritti
    // dal payload completo di lastSave() alla fine della compilazione.
    const domande = (onlineData?.compilazione?.domande || []).map((d: any) => ({
      domanda: d.domanda,
      risposta: d.risposta,
      tempo: d.tempo,
    }));

    const slim: any = {
      compilazione: {
        domande,
        last: onlineData?.compilazione?.last ?? -1,
      },
      lastUpdate: Date.now(),
    };

    if (onlineData?.compilazione?.concluso !== undefined) {
      slim.compilazione.concluso = onlineData.compilazione.concluso;
    }

    return slim;
  }

  /**
   * Hash deterministico (FNV-1a 32-bit) di un payload slim. Usato per dedupe:
   * se l'hash è identico all'ultima scrittura riuscita, skippiamo il sync.
   */
  private computeSlimHash(slim: any): string {
    const json = JSON.stringify(slim?.compilazione ?? {});
    let h = 0x811c9dc5;
    for (let i = 0; i < json.length; i++) {
      h ^= json.charCodeAt(i);
      h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
    }
    return h.toString(16);
  }

  /**
   * Sincronizza compilazione con Firestore (con retry automatico).
   * Public perché usato da flushAndLock() al finalizzazione.
   */
  async syncToBackend(
    questionnaireCode: string,
    user: any,
    onlineData: any,
    retryCount: number = 0
  ): Promise<void> {
    if (!navigator.onLine || this.effectivelyOffline) {
      this.saveStatus$.next(SaveStatus.OFFLINE);
      // Aggiungi alla coda per sync quando torna online
      this.syncQueue.push({ data: { questionnaireCode, user, onlineData }, retries: 0 });
      return;
    }

    this.syncInProgress = true;

    try {
      this.saveStatus$.next(SaveStatus.SAVING_BACKEND);

      if (!this.firstSyncDone[questionnaireCode]) {
        // Prima scrittura: payload completo (intestazione + stato).
        // Stampa `lastUpdate` anche qui: la prima risposta della sessione passa solo
        // da questo ramo (full, senza merge) e altrimenti il doc resterebbe senza
        // data di ultima modifica → "ultima attività" assente lato elaborazione.
        onlineData.lastUpdate = Date.now();
        // Usa addSubCollection (set senza merge) per garantire che il doc sia consistente.
        await this.fire.addSubCollection(
          'clienti',
          'licenze',
          'compilazioni',
          onlineData,
          user.azienda,
          user.licenza,
          user.reparto + user.utente
        );
        this.firstSyncDone[questionnaireCode] = true;
      } else {
        // Scritture successive: payload slim con merge: true per preservare l'intestazione.
        const slim = this.buildSlimPayload(onlineData);

        // Dedupe: se il payload è identico all'ultimo sincronizzato, skip.
        // Risparmia ~20-30% di write in scenari con molti focus/blur e
        // naviga rapida avanti/indietro tra domande senza modifiche.
        const hash = this.computeSlimHash(slim);
        if (this.lastSyncedHash[questionnaireCode] === hash) {
          this.syncInProgress = false;
          this.saveStatus$.next(SaveStatus.SAVED);
          setTimeout(() => {
            if (this.saveStatus$.value === SaveStatus.SAVED) {
              this.saveStatus$.next(SaveStatus.IDLE);
            }
          }, 1500);
          return;
        }

        await this.fire.db
          .collection('clienti')
          .doc(user.azienda)
          .collection('licenze')
          .doc(user.licenza)
          .collection('compilazioni')
          .doc(user.reparto + user.utente)
          .set(slim, { merge: true });

        // Aggiorna hash solo dopo scrittura riuscita
        this.lastSyncedHash[questionnaireCode] = this.computeSlimHash(slim);
      }

      // Aggiorna stato locale come sincronizzato
      const state = this.getLocalState(questionnaireCode);
      if (state) {
        state.synced = true;
        state.offline = false;
        this.saveLocalState(questionnaireCode, state);
      }

      this.lastSyncAt = Date.now();
      this.consecutiveFailures = 0;
      this.effectivelyOffline = false;
      this.saveStatus$.next(SaveStatus.SAVED);

      // Reset dopo 2 secondi
      setTimeout(() => {
        if (this.saveStatus$.value === SaveStatus.SAVED) {
          this.saveStatus$.next(SaveStatus.IDLE);
        }
      }, 2000);

      this.syncInProgress = false;

      // Se c'è un payload pendente arrivato durante il sync, ripianifica il debounce
      // con l'ultimo dato disponibile (coalescing: una sola scrittura al posto di N).
      if (this.pendingSync) {
        const next = this.pendingSync;
        this.pendingSync = null;
        this.debounceBackendSync(next.questionnaireCode, next.user, next.onlineData);
      }
    } catch (error: any) {
      this.syncInProgress = false;
      this.consecutiveFailures++;
      console.debug('Sync error:', error);

      // Dopo N fallimenti consecutivi, consideriamo la connessione inaffidabile:
      // smettiamo di scrivere e accumuliamo in coda fino a quando la rete non
      // torna stabile (evento "online" o sync riuscito).
      if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
        this.effectivelyOffline = true;
        // Connessione inaffidabile: disattiva rete Firestore per fermare lo spam di errori
        this.fire.db.firestore.disableNetwork().catch(() => {});
        this.saveStatus$.next(SaveStatus.OFFLINE);
        this.syncQueue.push({ data: { questionnaireCode, user, onlineData }, retries: 0 });
        return;
      }

      // Firestore: se la coda è satura NON ri-accodare (amplificherebbe il problema).
      // Lasciamo che il prossimo save (debounced) riprovi con l'ultimo stato.
      const isResourceExhausted =
        error?.code === 'resource-exhausted' ||
        (typeof error?.message === 'string' && error.message.includes('resource-exhausted'));

      if (isResourceExhausted) {
        // Forza il prossimo debounce ad aspettare l'intero MIN_SYNC_INTERVAL_MS
        this.lastSyncAt = Date.now();
        this.saveStatus$.next(SaveStatus.ERROR);
        setTimeout(() => {
          if (this.saveStatus$.value === SaveStatus.ERROR) {
            this.saveStatus$.next(SaveStatus.IDLE);
          }
        }, 3000);
        return;
      }

      // Non ritentare se finalizing è attivo — lastSave() ha priorità
      if (this.finalizing[questionnaireCode]) return;

      if (retryCount < this.MAX_RETRY_ATTEMPTS) {
        // Retry con backoff esponenziale (minimo 2s)
        const delay = Math.max(2000, Math.pow(2, retryCount) * 1000);
        this.retryTimer = setTimeout(() => {
          this.retryTimer = null;
          this.syncToBackend(questionnaireCode, user, onlineData, retryCount + 1);
        }, delay);
      } else {
        this.saveStatus$.next(SaveStatus.ERROR);
        // Aggiungi alla coda per retry manuale
        this.syncQueue.push({ data: { questionnaireCode, user, onlineData }, retries: retryCount });
      }
    }
  }

  /**
   * Debounce + coalescing: se un sync è già in corso, accumula solo l'ultimo
   * onlineData in pendingSync e NON accoda — la scrittura successiva partirà
   * al termine di quella in corso con lo stato più recente.
   */
  private debounceBackendSync(questionnaireCode: string, user: any, onlineData: any): void {
    // Se la compilazione sta finalizzando, blocca ogni ulteriore scrittura:
    // lastSave() si occupa del write finale.
    if (this.finalizing[questionnaireCode]) {
      return;
    }
    if (this.syncInProgress) {
      this.pendingSync = { questionnaireCode, user, onlineData };
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Cancella eventuale retry pendente: il nuovo payload lo supera
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    // Aggiorna sempre l'ultimo riferimento pendente così beforeunload/force hanno il dato fresco
    this.pendingSync = { questionnaireCode, user, onlineData };

    // Rate-limit floor: mai meno di MIN_SYNC_INTERVAL_MS tra due scritture.
    const sinceLast = Date.now() - this.lastSyncAt;
    const delay = Math.max(this.SYNC_DEBOUNCE_MS, this.MIN_SYNC_INTERVAL_MS - sinceLast);

    this.debounceTimer = setTimeout(() => {
      const next = this.pendingSync;
      this.pendingSync = null;
      this.debounceTimer = null;
      if (next) {
        this.syncToBackend(next.questionnaireCode, next.user, next.onlineData);
      } else {
        this.syncToBackend(questionnaireCode, user, onlineData);
      }
    }, delay);
  }

  /**
   * Salva stato in localStorage
   */
  private saveLocalState(questionnaireCode: string, state: CompilationState): void {
    try {
      localStorage.setItem(
        `${this.STORAGE_PREFIX}${questionnaireCode}`,
        JSON.stringify(state)
      );
    } catch (error) {
      console.debug('Error saving local state:', error as any);
      // Fallback: prova a rimuovere vecchi dati se quota esaurita
      const err: any = error;
      if (
        err &&
        (err.name === "QuotaExceededError" ||
          err.name === "NS_ERROR_DOM_QUOTA_REACHED")
      ) {
        this.cleanupOldStates();
        try {
          localStorage.setItem(
            `${this.STORAGE_PREFIX}${questionnaireCode}`,
            JSON.stringify(state)
          );
        } catch (retryError) {
          console.error('Failed to save after cleanup:', retryError);
        }
      }
    }
  }

  /**
   * Rimuove stato locale
   */
  clearLocalState(questionnaireCode: string): void {
    localStorage.removeItem(`${this.STORAGE_PREFIX}${questionnaireCode}`);
    delete this.firstSyncDone[questionnaireCode];
    delete this.lastSyncedHash[questionnaireCode];
    // NON resettare `finalizing[code]` qui: clearLocalState viene invocato
    // PRIMA della scrittura finale, quindi la flag deve restare attiva.
    // Il rilascio avviene via releaseFinalizeLock() in caso di errore.
  }

  /**
   * Cleanup atomico di tutte le chiavi localStorage legate a una compilazione.
   * Da chiamare a finalize per evitare chiavi orfane (es. `questLang_<code>`).
   */
  clearAllForCompilation(questionnaireCode: string): void {
    this.clearLocalState(questionnaireCode);
    try {
      localStorage.removeItem(`questLang_${questionnaireCode}`);
    } catch {
      // non bloccante
    }
  }

  /**
   * Pulisce stati vecchi per liberare spazio
   */
  private cleanupOldStates(): void {
    const maxAge = 7 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    // Snapshot delle chiavi: rimuovere durante l'iterazione per indice salterebbe la chiave successiva
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
    for (const key of keys) {
      if (key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const state = JSON.parse(localStorage.getItem(key) || '{}');
          if (now - state.lastUpdate > maxAge) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          // Ignora errori di parsing
        }
      }
    }
  }

  /**
   * Setup listener per eventi online/offline
   */
  private setupOnlineListener(): void {
    window.addEventListener('online', () => {
      this.consecutiveFailures = 0;
      this.effectivelyOffline = false;
      // Riattiva la rete Firestore per riprendere Listen/Write streams
      this.fire.db.firestore.enableNetwork().catch(() => {});
      this.saveStatus$.next(SaveStatus.IDLE);
      this.processSyncQueue();
    });

    window.addEventListener('offline', () => {
      // Disattiva la rete Firestore per fermare i retry che spammano errori
      this.fire.db.firestore.disableNetwork().catch(() => {});
      this.saveStatus$.next(SaveStatus.OFFLINE);
    });

    // Edge Sleeping Tabs / Efficiency Mode uccide le connessioni long-polling
    // SENZA evento online/offline (la rete OS non cambia): al risveglio
    // disableNetwork() (vedi syncToBackend) resterebbe attivo per sempre e ogni
    // lettura — anche le "Once" — verrebbe servita dalla cache IndexedDB stale.
    // enableNetwork() a rete già attiva è un no-op ma riavvia gli stream in backoff.
    const resumeNetwork = () => {
      if (!navigator.onLine) { return; }
      this.consecutiveFailures = 0;
      this.effectivelyOffline = false;
      this.fire.db.firestore.enableNetwork().catch(() => {});
      this.saveStatus$.next(SaveStatus.IDLE);
      this.processSyncQueue();
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') { resumeNetwork(); }
    });
    window.addEventListener('pageshow', (e: PageTransitionEvent) => {
      if (e.persisted) { resumeNetwork(); } // ripristino da bfcache
    });
  }

  /**
   * Processa coda di sincronizzazione
   */
  private async processSyncQueue(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine || this.effectivelyOffline || this.syncQueue.length === 0) {
      return;
    }

    this.syncInProgress = true;

    while (this.syncQueue.length > 0 && navigator.onLine && !this.effectivelyOffline) {
      const item = this.syncQueue.shift();
      if (item && item.retries < this.MAX_RETRY_ATTEMPTS) {
        try {
          await this.syncToBackend(
            item.data.questionnaireCode,
            item.data.user,
            item.data.onlineData,
            item.retries
          );
        } catch (error) {
          // Reinserisci in coda se fallisce
          item.retries++;
          this.syncQueue.push(item);
        }
      }
    }

    this.syncInProgress = false;

    // Riprova dopo 5 secondi se ci sono ancora item in coda
    if (this.syncQueue.length > 0) {
      setTimeout(() => this.processSyncQueue(), 5000);
    }
  }

  /**
   * Forza sincronizzazione immediata (per salvataggio finale)
   */
  async forceSync(questionnaireCode: string, user: any, onlineData: any): Promise<void> {
    // Cancella debounce pendente
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.pendingSync = null;

    await this.syncToBackend(questionnaireCode, user, onlineData);
  }

  /**
   * Marca una compilazione come "intestazione già presente sul backend".
   * Da chiamare quando il documento esiste già (es. ripresa di una compilazione)
   * per saltare la prima scrittura completa e usare subito payload slim con merge.
   */
  markIntestazionePersisted(questionnaireCode: string): void {
    this.firstSyncDone[questionnaireCode] = true;
  }

  /**
   * Flush + lock: cancella il debounce, attende un eventuale sync in corso e
   * blocca future scritture per `questionnaireCode`. Da chiamare PRIMA della
   * scrittura finale in lastSave() per eliminare race con il write incrementale.
   * Il lock viene rilasciato automaticamente da clearLocalState() o da releaseFinalizeLock().
   */
  async flushAndLock(questionnaireCode: string): Promise<void> {
    this.finalizing[questionnaireCode] = true;

    // Cancella debounce pendente e sincronizza i dati se presenti
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;

      // Importante: lancia il sync se c'è un pendingSync, altrimenti viene scartato
      const next = this.pendingSync;
      this.pendingSync = null;
      if (next) {
        try {
          await this.syncToBackend(next.questionnaireCode, next.user, next.onlineData);
        } catch (err) {
          // Se il sync fallisce, procedi comunque col write finale — i dati
          // locali potrebbero essere parzialmente sincronizzati su Firestore
          console.debug("[flushAndLock] sync fallito ma procedo con write finale", err);
        }
      }
    } else {
      this.pendingSync = null;
    }

    // Cancella eventuale retry in corso
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    // Attendi eventuale sync in corso (max ~8s, poi procede comunque)
    const started = Date.now();
    while (this.syncInProgress && Date.now() - started < 8000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    // Attendi un po' per dare tempo al listener di Firestore di aggiornare il dato locale
    await new Promise((resolve) => setTimeout(resolve, 200));
  }

  /**
   * Rilascia il lock di finalizzazione (utile in caso di errore/retry manuale).
   */
  releaseFinalizeLock(questionnaireCode: string): void {
    delete this.finalizing[questionnaireCode];
  }

  /**
   * Verifica se siamo offline
   */
  isOffline(): boolean {
    return !navigator.onLine;
  }
}
